import {useEffect, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {
  Button,
  Input,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {PlusOutlined, ReloadOutlined, SearchOutlined} from '@ant-design/icons';
import moment from 'moment';
import {get} from 'lodash';
import Highlighter from 'react-highlight-words';

import {EnrollmentsService} from './EnrollmentsService';
import {
  EnrollmentsPaginatedResponse,
  EnrichedEnrollment,
} from './IEnrollmentsService';

import {useTablePagination} from '../../hooks/useTablePagination';

const {Title, Text} = Typography;

const enrollmentsService = new EnrollmentsService();

const PLACEMENT_STATUS_COLOR: Record<string, string> = {
  DONE: 'success',
  DOING: 'warning',
  TODO: 'error',
};

export const EnrollmentsTable = () => {
  const [enrollments, setEnrollments] =
    useState<EnrollmentsPaginatedResponse>();
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [searchedColumn, setSearchedColumn] = useState<string>('');
  const [searchText, setSearchText] = useState('');
  const {
    current,
    setCurrent,
    pageSize,
    total,
    setTotal,
    paginationConfig,
    paginationSelector,
  } = useTablePagination({itemName: 'enrollments'});

  const searchInputRef = useRef<any>(null);

  const fetchEnrollments = (page: number, limit: number) => {
    setIsLoading(true);
    enrollmentsService
      .getEnrollments(page, limit)
      .then(resp => {
        setEnrollments(resp);
        // Try to get total from response, fallback to items length heuristic
        setTotal(resp.total ?? resp.items.length * 4); // adjust if your API returns total
        setIsLoading(false);
      })
      .catch(() => {
        setIsError(true);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchEnrollments(current, pageSize);
  }, [current, pageSize]);

  // ── Search helpers ──────────────────────────────────────────────
  const handleSearch = (
    selectedKeys: any[],
    confirm: () => void,
    dataIndex: string,
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText('');
    setSearchedColumn('');
  };

  const getColumnSearchProps = (dataIndex: string) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }: any) => (
      <div style={{padding: 8}} onKeyDown={e => e.stopPropagation()}>
        <Input
          ref={searchInputRef}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{marginBottom: 8, display: 'block'}}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{width: 90}}>
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{width: 90}}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{color: filtered ? '#1890ff' : undefined}} />
    ),
    onFilter: (value: any, record: any) =>
      get(record, dataIndex)
        ?.toString()
        .toLowerCase()
        .includes(value.toLowerCase()) ?? false,
    onFilterDropdownOpenChange: (visible: boolean) => {
      if (visible) setTimeout(() => searchInputRef.current?.select(), 100);
    },
    render: (text: string) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{backgroundColor: '#ffc069', padding: 0}}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text?.toString() ?? ''}
        />
      ) : (
        text
      ),
  });

  // ── Columns ─────────────────────────────────────────────────────
  const columns: any[] = [
    {
      title: 'ID',
      dataIndex: '_id',
      key: '_id',
      width: 120,
      ...getColumnSearchProps('_id'),
      render: (id: string) => (
        <Tooltip title={id}>
          <Link
            to={`/enrollments/${id}`}
            style={{fontFamily: 'monospace', fontSize: 12}}>
            {id?.slice(-8)}
          </Link>
        </Tooltip>
      ),
    },
    {
      title: 'Student',
      dataIndex: ['student', 'name'],
      ...getColumnSearchProps('student.name'),
      sorter: (a: any, b: any) =>
        a.student?.name?.localeCompare(b.student?.name),
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (_: any, record: EnrichedEnrollment) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.student?.name ?? '-'}</Text>
          <Text type="secondary" style={{fontSize: 11}}>
            {record.student?.id?.split('-')[0]}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      sorter: (a: any, b: any) =>
        moment(a.startDate).unix() - moment(b.startDate).unix(),
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (date: string) =>
        date ? (
          <Space direction="vertical" size={0}>
            <Text>{moment(date).format('DD MMM YYYY')}</Text>
            <Text type="secondary" style={{fontSize: 11}}>
              {moment(date).fromNow()}
            </Text>
          </Space>
        ) : (
          '-'
        ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      defaultSortOrder: 'descend',
      sorter: (a: any, b: any) =>
        moment(a.createdAt).unix() - moment(b.createdAt).unix(),
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (date: string) =>
        date ? (
          <Tooltip title={moment(date).format('DD MMM YYYY HH:mm')}>
            <Text>{moment(date).fromNow()}</Text>
          </Tooltip>
        ) : (
          '-'
        ),
    },
    {
      title: 'Placement',
      dataIndex: 'placementStatus',
      sorter: (a: any, b: any) =>
        a.placementStatus?.localeCompare(b.placementStatus),
      sortDirections: ['ascend', 'descend', 'ascend'],
      filters: [
        {text: 'TODO', value: 'TODO'},
        {text: 'DOING', value: 'DOING'},
        {text: 'DONE', value: 'DONE'},
      ],
      onFilter: (value: any, record: any) => record.placementStatus === value,
      render: (status: string) => (
        <Tag color={PLACEMENT_STATUS_COLOR[status] ?? 'default'}>
          {status ?? '-'}
        </Tag>
      ),
    },
    {
      title: 'Free?',
      dataIndex: 'isFree',
      width: 80,
      filters: [
        {text: 'Free', value: true},
        {text: 'Paid', value: false},
      ],
      onFilter: (value: any, record: any) => record.isFree === value,
      render: (isFree: boolean) => (
        <Tag color={isFree ? 'green' : 'red'}>{isFree ? 'Free' : 'Paid'}</Tag>
      ),
    },
  ];

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div style={{padding: '16px 24px'}}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}>
        <Title level={3} style={{margin: 0}}>
          Enrollments
          {!isLoading && total > 0 && (
            <Text
              type="secondary"
              style={{fontSize: 14, fontWeight: 400, marginLeft: 8}}>
              ({total} total)
            </Text>
          )}
        </Title>
        <Space>
          {/* Page size selector */}
          {paginationSelector}
          <Tooltip title="Refresh">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchEnrollments(current, pageSize)}
              loading={isLoading}
            />
          </Tooltip>
          <Button type="primary" icon={<PlusOutlined />}>
            <Link to="/enrollments/create" style={{color: 'inherit'}}>
              Create Enrollment
            </Link>
          </Button>
        </Space>
      </div>

      {/* Table */}
      {isError ? (
        <div style={{textAlign: 'center', padding: 48}}>
          <Title level={4}>⚠️ Failed to load enrollments</Title>
          <Button onClick={() => fetchEnrollments(current, pageSize)}>
            Try Again
          </Button>
        </div>
      ) : (
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={enrollments?.items}
          loading={isLoading}
          scroll={enrollments?.items?.length ? {x: 800} : undefined}
          pagination={paginationConfig}
        />
      )}
    </div>
  );
};
